<script setup lang="ts">
import { email as emailRule, required } from '@vee-validate/rules';
import { Form } from 'vee-validate';
import { computed } from 'vue';
import { useStore } from 'vuex';

import { subscribeCustomer } from '@/api/contacts';
import { applyDiscountCodeToken } from '@/api/discounts';
import FormInput from '@/components/base/form/FormInput.vue';
import ThemedButton from '@/components/base/ThemedButton.vue';
import BaseBodyText from '@/components/base/typography/BaseBodyText.vue';
import Caption from '@/components/base/typography/Caption.vue';
import Header3 from '@/components/base/typography/Header3.vue';
import LargeBodyText from '@/components/base/typography/LargeBodyText.vue';
import { OnsiteOfferConfig } from '@/composables/personalization/useOnsiteOffer';
import { useCallback } from '@/composables/useCallback';
import { useCart } from '@/composables/useCart';
import { FlexibleMessageValidator, useForm } from '@/composables/useForm';
import { USER_EMAIL_COOKIE } from '@/lib/personalization/common';
import { useCustomer } from '@/stores/customer';
import { sendButtonClicked } from '@/utils/analytics/buttonClicked';
import { sendContactSubscribed } from '@/utils/analytics/contactSubscribed';
import { setCookie } from '@/utils/isomorphic/cookie';
import { reportError } from '@/utils/reportError';

const { analyticsEventName, initialScreen } = defineProps<{
  analyticsEventName: string;
  initialScreen: NonNullable<OnsiteOfferConfig['initialScreen']>;
}>();

const emit = defineEmits<{
  'email-signup-completed': [];
}>();

const emailDiscountApplied = defineModel<boolean>('emailDiscountApplied');
const emailInput = defineModel<string>('emailInput');
const existingContact = defineModel<boolean>('existingContact');
const smsSubscribed = defineModel<boolean>('smsSubscribed');

const backgroundImageDesktop = computed(() => `url('${initialScreen.mainImageDesktopSrc}')`);
const backgroundImageMobile = computed(() => `url('${initialScreen.mainImageMobileSrc}')`);

const store = useStore();
const { setCart } = useCart(store);
const customerStore = useCustomer();
const { errorMessages, validatorFailed } = useForm();

const pageSection = 'FTB Popup';

const rules: Record<'email', FlexibleMessageValidator<string>> = {
  email: (value) => {
    if (!required(value)) return errorMessages.email.required;
    if (!emailRule(value)) return errorMessages.email.email;
    return true;
  },
};

async function applyCoupon() {
  let response;
  try {
    response = await applyDiscountCodeToken(initialScreen.couponToken);
  } catch (error) {
    reportError(error);
  }

  if (response?.cart) {
    setCart(response.cart);
  }
}

const onSubmit = useCallback(async () => {
  sendButtonClicked({
    buttonName: 'FTB Initial Screen - Submit',
    content: initialScreen.buttonCta,
    pageSection,
  });

  const email = emailInput.value;

  // email is guaranteed by form validation, so this is just to appease TS
  if (!email) return;

  const response = await subscribeCustomer({
    email,
    reason: analyticsEventName,
  });

  if (response.contact) {
    customerStore.contact = response.contact;
  }

  if (response.existingCustomer === true || response.existingContact === true) {
    existingContact.value = true;
    smsSubscribed.value = response.contact?.smsSubscribed ?? false;
  } else {
    existingContact.value = false;
    await applyCoupon();
    emailDiscountApplied.value = true;
    setCookie(USER_EMAIL_COOKIE, email);
  }

  sendContactSubscribed({
    email,
    reason: analyticsEventName,
    source: analyticsEventName,
  });

  customerStore.sendIdentifyEvent();

  emit('email-signup-completed');
});
</script>

<template>
  <div class="flex flex-col justify-center items-center md:flex-row">
    <div class="flex-1 px-6 py-6">
      <Header3>{{ initialScreen.headline }}</Header3>
      <LargeBodyText class="mt-4 font-bold md:mt-7">
        {{ initialScreen.caption }}
      </LargeBodyText>
      <div class="mt-4 w-full md:mt-7">
        <Form v-slot="{ meta: formEmailMeta }" @submit="onSubmit.execute()" class="grid gap-4">
          <FormInput
            v-model="emailInput"
            class="w-full"
            :inputAttributes="{
              autocomplete: 'email',
              name: 'email',
              placeholder: 'Email Address*',
              type: 'email',
            }"
            showPlaceholder
            theme="dark"
            :validator="rules.email"
          />
          <ThemedButton
            class="w-full h-12"
            :disabled="(formEmailMeta.touched && validatorFailed(formEmailMeta)) ?? false"
            :isLoading="onSubmit.isPending"
            size="default"
            theme="dark-yellow"
            type="submit"
          >
            {{ initialScreen.buttonCta }}
          </ThemedButton>
        </Form>
        <BaseBodyText v-if="onSubmit.error" class="mt-4 text-red-500">
          {{ onSubmit.error }}
        </BaseBodyText>
        <Caption class="mt-4 font-semibold text-neutral-500">A wee-bit of fine print:</Caption>
        <Caption class="mt-2 text-neutral-500">
          Offer valid for new subscribers shipping to destinations within the contiguous USA. Free
          shipping applies to standard, "ground" shipping methods. Existing email subscriber? Check
          your email inbox weekly for other great offers
        </Caption>
      </div>
    </div>
    <div class="flex-1 w-full h-full bg-center bg-no-repeat bg-cover dynamic-bg" />
  </div>
</template>

<style scoped>
.dynamic-bg {
  background-image: v-bind(backgroundImageMobile);
}
@media (min-width: 768px) {
  .dynamic-bg {
    background-image: v-bind(backgroundImageDesktop);
  }
}
</style>
